import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUser } from '@clerk/react'
import { useQuery } from '@tanstack/react-query'
import { useAuthedApi } from '../../hooks/useAuthedApi'

interface UserProfile {
  id: string
  role: 'ADMIN' | 'ESTIMATOR'
}

interface SidebarProps {
  onClose?: () => void
}

export default function Sidebar({ onClose }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isSignedIn } = useUser()
  const { get } = useAuthedApi()
  const [collapsed, setCollapsed] = useState(false)
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const { data: userProfile } = useQuery<UserProfile>({
    queryKey: ['userProfile'],
    queryFn: () => get<UserProfile>('/auth/profile'),
    enabled: !!isSignedIn,
  })

  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (windowWidth < 768 && onClose) {
      onClose()
    }
  }, [location.pathname, windowWidth, onClose])

  // Add mobile overlay that closes sidebar when clicked
  const isMobile = windowWidth < 768

  const handleNavigate = (path: string) => {
    navigate(path)
    if (isMobile && onClose) {
      onClose()
    }
  }

  const isActive = (path: string) => location.pathname.startsWith(path)

  const baseNavItems = [
    { id: 'home', icon: '🏠', label: 'Home', path: '/dashboard' },
    { id: 'contractors', icon: '👥', label: 'General Contractors', path: '/contractors' },
    { id: 'projects', icon: '📋', label: 'Projects', path: '/projects' },
    { id: 'integrations', icon: '🔗', label: 'Integrations', path: '/integrations' },
    { id: 'settings', icon: '⚙️', label: 'Settings', path: '/settings/pricing' },
  ]

  const adminNavItems = [
    { id: 'users', icon: '👤', label: 'Users', path: '/users' },
    { id: 'approvals', icon: '✓', label: 'Approvals', path: '/approvals' },
  ]

  const navItems = userProfile?.role === 'ADMIN' ? [...baseNavItems.slice(0, 3), ...adminNavItems, ...baseNavItems.slice(3)] : baseNavItems

  return (
    <>
      {/* Mobile overlay/scrim - click to close */}
      {isMobile && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            top: 'var(--nav-height)',
          }}
          aria-label="Close menu"
        />
      )}

      <aside
        style={{
          position: isMobile ? 'fixed' : 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          width: collapsed ? 70 : 260,
          background: 'var(--ink)',
          color: 'white',
          padding: '20px 0',
          overflowY: 'auto',
          transition: 'width 0.3s',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      {/* Logo/Brand */}
      <div
        style={{
          padding: '0 16px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          textAlign: 'center',
        }}
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: collapsed ? 24 : 20,
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          {collapsed ? '≡' : 'EstimatePro'}
        </button>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => handleNavigate(item.path)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: isActive(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
              border: 'none',
              borderLeft: isActive(item.path) ? '3px solid var(--green)' : '3px solid transparent',
              color: isActive(item.path) ? 'white' : 'rgba(255,255,255,0.7)',
              fontSize: 14,
              fontWeight: isActive(item.path) ? 600 : 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              transition: 'all 0.2s',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              if (!isActive(item.path)) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                e.currentTarget.style.color = 'white'
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive(item.path)) {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'rgba(255,255,255,0.7)'
              }
            }}
          >
            <span style={{ fontSize: 20, minWidth: 24 }}>{item.icon}</span>
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* User Info */}
      <div
        style={{
          padding: '16px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          textAlign: 'center',
        }}
      >
        {!collapsed && user && (
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: 0 }}>
            {user.fullName || user.emailAddresses[0]?.emailAddress}
          </p>
        )}
      </div>
      </aside>
    </>
  )
}
